// The form layer (docs/UI-REMEDIATION-PLAN.md §4.3, theme T4). Before it, the
// only complete field was private to the auth screens, the shared Input could
// not hold a value, and nine screens each drew their own: nine field recipes,
// eight label recipes, no aria-invalid or aria-describedby anywhere (DS-11,
// XA-12, HRP-11, ORG-18, WS-12, TS-V3).
//
//   Field      label + hint + error, wired to the control by id
//   TextInput  / TextArea — controlled, native props forwarded, the .hf-field frame
//   DateField  a labelled native date input that echoes the day in the org's format
//   EditableField, Stepper — moved here from people.tsx (still re-exported there)
//
// The frame's paint and focus state live in tokens.css (.hf-field): an inline
// style cannot express :focus-within, and would beat the rule that can.
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { Button, IconButton } from './primitives';
import { fmtDate } from '../lib/dates';
import { useOrgDates } from '../lib/orgSettings';

// What a Field hands its control: the id its <label> points at, the ids that
// describe it, and its invalid/required state.
type FieldControl = {
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
  'aria-required'?: true;
};
const FieldContext = createContext<FieldControl | null>(null);

// The uppercase label recipe, on the type scale. Was eight recipes in six sizes.
const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--hf-text-2xs)',
  fontWeight: 'var(--hf-weight-medium)',
  letterSpacing: 'var(--hf-track-eyebrow)',
  textTransform: 'uppercase',
  color: 'var(--hf-ink-3)',
};
const noteStyle: CSSProperties = {
  fontSize: 'var(--hf-text-xs)',
  lineHeight: 'var(--hf-leading-snug)',
};

/**
 * A labelled form control with an optional hint and error.
 *
 * TextInput and TextArea inside it pick up the wiring by themselves. Any other
 * control takes the render form — `{(control) => <Select {...control} … />}` —
 * so its id, aria-describedby, aria-invalid and aria-required come from here.
 *
 * `<label htmlFor>`, not a label wrapped round the control: a wrapping label
 * also swallowed the trailing slot, so the auth password box was named
 * "Password Show password".
 */
export const Field = ({
  label,
  hint,
  error,
  required,
  children,
  style,
}: {
  label: string;
  hint?: ReactNode;
  /** The message, not a flag: the border alone told a screen reader nothing (SH-13, XA-10). */
  error?: string;
  /** aria-required on the control. No visual-only asterisk. */
  required?: boolean;
  children: ReactNode | ((control: FieldControl) => ReactNode);
  style?: CSSProperties;
}) => {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : '', error ? errorId : ''].join(' ').trim();
  // Only the keys that apply: an `'aria-invalid': undefined` spread after a
  // control's own props would erase an aria-invalid the caller set on it.
  const control: FieldControl = {
    id,
    ...(describedBy ? { 'aria-describedby': describedBy } : {}),
    ...(error ? { 'aria-invalid': true as const } : {}),
    ...(required ? { 'aria-required': true as const } : {}),
  };
  // role="alert" only once the error has changed since mount. An error that is
  // already there when the form opens is described by the field, not shouted
  // over whatever the screen reader was reading; one caused by the last action
  // is announced. (Setting state during render is React's pattern for "the prop
  // changed": https://react.dev/reference/react/useState#storing-information-from-previous-renders)
  const [seen, setSeen] = useState(error);
  const [changed, setChanged] = useState(false);
  if (error !== seen) {
    setSeen(error);
    setChanged(true);
  }
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--hf-space-2)',
        minWidth: 0,
        ...style,
      }}
    >
      <label htmlFor={id} style={labelStyle}>
        {label}
      </label>
      {typeof children === 'function' ? (
        children(control)
      ) : (
        <FieldContext.Provider value={control}>{children}</FieldContext.Provider>
      )}
      {hint && (
        <div id={hintId} style={{ ...noteStyle, color: 'var(--hf-ink-3)' }}>
          {hint}
        </div>
      )}
      {error && (
        <div
          id={errorId}
          role={changed ? 'alert' : undefined}
          style={{ ...noteStyle, color: 'var(--hf-rose-text)' }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

// The frame both text controls sit in. 44px by default — the auth field's
// height, a comfortable touch target; `dense` is the in-row size for tables and
// settings rows.
const frameStyle = (dense: boolean | undefined, multiline?: boolean): CSSProperties => ({
  display: 'flex',
  alignItems: multiline ? 'stretch' : 'center',
  gap: 'var(--hf-space-2)',
  padding: dense || multiline ? 'var(--hf-pad-field)' : '0 var(--hf-space-3)',
  minHeight: dense ? undefined : 44,
  minWidth: 0,
});
// The frame is what looks like the control, but only the <input> inside took a
// press: a dense row's input is ~20px tall in a ~30px frame, short of WCAG
// 2.5.8's 24 on a tablet. A press on the frame's own padding focuses its field.
// mousedown, which a tap also fires; preventDefault keeps the frame from taking
// the focus back.
const focusOwnControl = (e: MouseEvent<HTMLDivElement>) => {
  if (e.target !== e.currentTarget) return;
  const control = e.currentTarget.querySelector<HTMLElement>('input, textarea');
  if (!control || (control as HTMLInputElement).disabled) return;
  e.preventDefault();
  control.focus();
};
// font: inherit, because form controls don't: the UA gives them their own face,
// weight and tracking, which is how fields drifted from the body type.
const controlStyle = (mono: boolean | undefined): CSSProperties => ({
  flex: 1,
  minWidth: 0,
  padding: 0,
  background: 'transparent',
  border: 0,
  color: 'var(--hf-ink)',
  fontFamily: mono ? 'var(--hf-font-mono)' : 'inherit',
  fontWeight: 'inherit',
  letterSpacing: 'inherit',
  fontSize: 'var(--hf-text-md)',
  lineHeight: 'var(--hf-leading-body)',
});

type NativeInput = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'size' | 'style'
>;

/** A controlled single-line input in the .hf-field frame. */
export const TextInput = ({
  value,
  onChange,
  leading,
  trailing,
  mono,
  dense,
  style,
  ...native
}: NativeInput & {
  value: string;
  onChange: (v: string) => void;
  /** Inside the frame, before the text: a search box's magnifier (design sync,
   *  2026-10-12). Decorative, so mark it aria-hidden. */
  leading?: ReactNode;
  /** Inside the frame, after the text: a reveal toggle, a unit, a clear button. */
  trailing?: ReactNode;
  mono?: boolean;
  dense?: boolean;
  /** On the frame, not the <input>. */
  style?: CSSProperties;
}) => {
  const control = useContext(FieldContext);
  return (
    <div
      className="hf-field"
      style={{ ...frameStyle(dense), ...style }}
      onMouseDown={focusOwnControl}
    >
      {leading}
      <input
        {...native}
        {...control}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={controlStyle(mono)}
      />
      {trailing}
    </div>
  );
};

/**
 * A labelled date field: the browser's own picker, and the chosen day echoed
 * under it in the org's format with its weekday ("Mon, 12-10-2026").
 *
 * A native date input follows the browser's locale and can't be told the
 * org's format, so /me put "joined 22-04-2019" above From/To boxes reading
 * mm/dd/yyyy, and Org's "Today reads 05-10-2026" sat over one too: a leave
 * booked for the wrong month waiting to happen (critique 2026-10-05 #2,
 * UI-CONVENTIONS §2). The echo is the Field's hint, so it is read with the
 * field (aria-describedby), ahead of any hint of the caller's, and there is
 * none while the field is empty. Before a range is submitted, say it whole
 * with fmtDayRange (lib/dates).
 */
export const DateField = ({
  label,
  hint,
  error,
  required,
  style,
  value,
  onChange,
  dense,
  ...native
}: Omit<NativeInput, 'type'> & {
  label: string;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  /** On the Field, not the input. */
  style?: CSSProperties;
  /** 'YYYY-MM-DD', or '' for no date. */
  value: string;
  onChange: (v: string) => void;
  dense?: boolean;
}) => {
  const { dateFormat } = useOrgDates();
  // The input only ever holds a whole date or ''; fmtDate's "—" is not a day.
  const day = value ? fmtDate(value, dateFormat, { weekday: true }) : '';
  const echo = day === '—' ? '' : day;
  return (
    <Field
      label={label}
      error={error}
      required={required}
      style={style}
      hint={
        echo || hint ? (
          <>
            {echo && (
              <span style={{ fontFamily: 'var(--hf-font-mono)', color: 'var(--hf-ink-2)' }}>
                {echo}
              </span>
            )}
            {echo && hint && ' · '}
            {hint}
          </>
        ) : undefined
      }
    >
      <TextInput {...native} type="date" mono dense={dense} value={value} onChange={onChange} />
    </Field>
  );
};

type NativeTextArea = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'onChange' | 'style'
>;

/** A controlled textarea in the .hf-field frame; `autoResize` grows it with its text (Design Spec P04). */
export const TextArea = ({
  value,
  onChange,
  autoResize,
  mono,
  rows = 3,
  style,
  ...native
}: NativeTextArea & {
  value: string;
  onChange: (v: string) => void;
  autoResize?: boolean;
  mono?: boolean;
  /** On the frame, not the <textarea>. */
  style?: CSSProperties;
}) => {
  const control = useContext(FieldContext);
  const ref = useRef<HTMLTextAreaElement>(null);
  // Measure before paint, so the box never flashes a scrollbar at the old height.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!autoResize || !el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value, autoResize]);
  return (
    <div
      className="hf-field"
      style={{ ...frameStyle(true, true), ...style }}
      onMouseDown={focusOwnControl}
    >
      <textarea
        {...native}
        {...control}
        ref={ref}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          ...controlStyle(mono),
          resize: autoResize ? 'none' : 'vertical',
          overflow: autoResize ? 'hidden' : undefined,
        }}
      />
    </div>
  );
};

// ── Editable field: the value, until you choose to change it ─────────────────
// Moved from people.tsx (re-exported there), and upgraded:
//  - Named. The label was a sibling <span> and the button was named by its value,
//    so a screen reader heard "—, button" (ME-07, HRP-04, XA-09). Now a real
//    <label htmlFor>, and the button is "Edit {label}", described by the value.
//  - Stakes. `commitOn="explicit"` shows Save and Cancel and ignores blur, for
//    salary, bank and role, which saved the moment focus left the box (HRP-02,
//    ME-05). Blur stays the default for low-stakes text.
//  - Honest. `onSaveAsync` resolves to {error?}: the field shows saving, then
//    "Saved", or keeps the draft and shows the error. A refused save used to
//    look like a success until reload (HRP-V1).
//  - Focus comes back. Enter, Escape, Save and Cancel each unmount the control
//    that has focus; the "Edit" button takes it back, where it used to fall to
//    <body> and the keyboard started again from the top of the page.
// Kept from Phase 0: only a real change is saved (HRP-V3), and without a save
// handler it is plain text — nothing to tab to or click (HRP-V1).
export type SaveResult = { error?: string };
type Status = 'idle' | 'saving' | 'saved';

export const EditableField = ({
  label,
  hideLabel,
  value,
  onSave,
  onSaveAsync,
  commitOn = 'blur',
  mono,
  multiline,
  prefix,
  placeholder = '—',
  type,
}: {
  /** Visible label and the control's name. An empty string leaves the field
   *  unnamed: pass the text with `hideLabel` when the row shows it elsewhere. */
  label: string;
  hideLabel?: boolean;
  value: string;
  /** Fire-and-forget: the field closes at once, and reporting is the caller's. */
  onSave?: (v: string) => void;
  /** Saving… then "Saved", or the draft kept with the error. Opt-in and typed
   *  rather than sniffed from onSave's return: an async handler passed through
   *  a `=> void` prop resolves to undefined whether or not the write landed,
   *  and would show a false "Saved" — HRP-V1 itself. Wins over `onSave`. */
  onSaveAsync?: (v: string) => Promise<SaveResult>;
  commitOn?: 'blur' | 'explicit';
  mono?: boolean;
  multiline?: boolean;
  prefix?: string;
  placeholder?: string;
  /** Native input type — 'date' gives a real picker, 'tel'/'email' the right
   *  mobile keyboard. Ignored when `multiline` (that renders a textarea). */
  type?: 'date' | 'tel' | 'email';
}) => {
  const id = useId();
  const valueId = `${id}-value`;
  const errorId = `${id}-error`;
  const editable = !!(onSave || onSaveAsync);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string>();
  const editRef = useRef<HTMLButtonElement>(null);
  // Refs, not state, because a handler can run on a stale render: removing the
  // focused input fires a blur (Chrome does) from the render that had it open.
  // `open` makes that late blur a no-op instead of a second save.
  const open = useRef(false);
  const refocus = useRef(false);

  // "Saved" is a receipt, not a state to keep: it clears itself.
  useEffect(() => {
    if (status !== 'saved') return;
    const t = setTimeout(() => setStatus('idle'), 2500);
    return () => clearTimeout(t);
  }, [status]);

  // Back to "Edit" after a close the user made, but only if focus was lost. A
  // blur close leaves focus where they sent it, and a slow save must not pull
  // it back from wherever they have gone since.
  useEffect(() => {
    if (editing || !refocus.current) return;
    refocus.current = false;
    const at = document.activeElement;
    if (!at || at === document.body) editRef.current?.focus();
  }, [editing]);

  // The draft starts from the value each time, so a value that changed while
  // the field was closed is what you edit.
  const start = () => {
    open.current = true;
    setDraft(value);
    setError(undefined);
    setStatus('idle');
    setEditing(true);
  };
  // `byUser`: Enter, Escape, Save or Cancel. Not a blur.
  const close = (byUser: boolean) => {
    open.current = false;
    refocus.current = byUser;
    setError(undefined);
    setEditing(false);
  };
  const commit = async (byUser: boolean) => {
    if (!open.current || status === 'saving') return;
    // Only a real change is saved. Clicking into a field and away again used to
    // write the unchanged value back — and contact and compensation both carry
    // audit triggers, so HR browsing a record left phantom "updates" behind.
    if (draft.trim() === value.trim()) return close(byUser);
    if (!onSaveAsync) {
      onSave?.(draft);
      return close(byUser);
    }
    setStatus('saving');
    const res = await onSaveAsync(draft).catch((e: unknown): SaveResult => ({
      error: e instanceof Error && e.message ? e.message : 'Couldn’t save. Try again.',
    }));
    if (res?.error) {
      // Keep the draft: retyping it is the user's cost for our failure.
      setStatus('idle');
      setError(res.error);
      return;
    }
    setStatus('saved');
    close(byUser);
  };
  // The field uses these keys, so the surface around it never sees them
  // (UI-CONVENTIONS §4): Escape cancels this edit and must not also close the
  // Dialog the field sits in, taking the whole form with it; Enter saves this
  // field and must not also submit that form.
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const enter = e.key === 'Enter' && !multiline;
    if (e.key !== 'Escape' && !enter) return;
    e.preventDefault();
    e.stopPropagation();
    if (enter) return void commit(true);
    // Escape: not mid-save. Cancel is disabled then too; the write is already out.
    if (open.current && status !== 'saving') close(true);
  };

  // Field's 11px eyebrow: it kept a 9.5px label until the floor was decided
  // (UI-REMEDIATION-PLAN §8.1, 2026-10-08).
  const ownLabel: CSSProperties = { ...labelStyle, fontWeight: 'inherit' };
  const size = 'var(--hf-text-md)';
  const font = mono ? 'var(--hf-font-mono)' : 'inherit';
  // The same box in view and edit, so opening a field doesn't shift the row:
  // the text sits where it sat, inside the frame's padding.
  const box: CSSProperties = {
    padding: 'var(--hf-space-1) var(--hf-space-2)',
    margin: '0 calc(-1 * var(--hf-space-2))',
  };
  const valueStyle: CSSProperties = {
    ...box,
    display: 'block',
    flex: '1 1 auto',
    minWidth: 0,
    border: '1px solid transparent',
    borderRadius: 'var(--hf-radius-md)',
    color: value ? 'var(--hf-ink)' : 'var(--hf-ink-3)',
    fontSize: size,
    fontFamily: font,
    lineHeight: 'var(--hf-leading-body)',
    whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };
  const shown = (
    <span id={valueId}>
      {prefix && (
        <span style={{ color: 'var(--hf-ink-3)', marginRight: 'var(--hf-space-1)' }}>{prefix}</span>
      )}
      {value || placeholder}
    </span>
  );

  const described = error ? errorId : undefined;
  const onBlur = commitOn === 'blur' ? () => void commit(false) : undefined;
  const editor = multiline ? (
    <TextArea
      id={id}
      autoFocus
      value={draft}
      onChange={setDraft}
      mono={mono}
      readOnly={status === 'saving'}
      aria-invalid={error ? true : undefined}
      aria-describedby={described}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      style={box}
    />
  ) : (
    <TextInput
      id={id}
      autoFocus
      type={type}
      value={draft}
      onChange={setDraft}
      mono={mono}
      dense
      readOnly={status === 'saving'}
      aria-invalid={error ? true : undefined}
      aria-describedby={described}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      style={box}
    />
  );

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-2)', minWidth: 0 }}
    >
      {label &&
        (editable ? (
          <label htmlFor={id} className={hideLabel ? 'hf-sr-only' : undefined} style={ownLabel}>
            {label}
          </label>
        ) : (
          <span className={hideLabel ? 'hf-sr-only' : undefined} style={ownLabel}>
            {label}
          </span>
        ))}
      {!editable ? (
        <span style={valueStyle}>{shown}</span>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          {editing ? (
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--hf-space-2)',
              }}
            >
              {editor}
              {error && (
                <div
                  id={errorId}
                  role="alert"
                  style={{ ...noteStyle, color: 'var(--hf-rose-text)' }}
                >
                  {error}
                </div>
              )}
              {commitOn === 'explicit' && (
                <div style={{ display: 'flex', gap: 'var(--hf-space-2)' }}>
                  <Button
                    variant="primary"
                    size="sm"
                    loading={status === 'saving'}
                    onClick={() => void commit(true)}
                  >
                    Save
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => close(true)}
                    disabled={status === 'saving'}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <button
              ref={editRef}
              type="button"
              id={id}
              onClick={start}
              aria-label={label ? `Edit ${label}` : undefined}
              aria-describedby={label ? valueId : undefined}
              title="Click to edit"
              style={{
                ...valueStyle,
                textAlign: 'left',
                background: 'transparent',
                cursor: 'text',
              }}
            >
              {shown}
            </button>
          )}
          {/* One live region, always mounted, whose text changes: a region
              inserted together with its text is mostly not announced. */}
          <span
            role="status"
            style={{
              ...noteStyle,
              flexShrink: 0,
              whiteSpace: 'nowrap',
              paddingLeft: status === 'idle' ? 0 : 'var(--hf-space-2)',
              color: status === 'saved' ? 'var(--hf-sage-text)' : 'var(--hf-ink-3)',
            }}
          >
            {status === 'saving' ? 'Saving…' : status === 'saved' ? 'Saved' : ''}
          </span>
        </div>
      )}
    </div>
  );
};

// ── Stepper ───────────────────────────────────────────────────────────────────
// Moved from people.tsx (re-exported there). The value was a <span> between two
// buttons named "−" and "+": no typing, no arrow keys, no bounds, one network
// write per click — 83 clicks to set 90 days (ORG-12, HRP-04, XA-09). Now it is
// a native number input between named ± buttons, unavailable at the bounds.
// Typing commits on blur or Enter, never per keystroke (typing "120" used to be
// three writes); Escape puts the value back. A ± click is one deliberate step
// and commits at once, as before.
const snap = (n: number, min: number, max: number, step: number) => {
  const stepped = Math.round((n - min) / step) * step + min;
  // Float noise: 0.1 + 0.2 steps must land on 0.3, not 0.30000000000000004.
  return Math.min(max, Math.max(min, Math.round(stepped * 1e6) / 1e6));
};

export const Stepper = ({
  label,
  value,
  onChange,
  min = 0,
  max = 999,
  step = 1,
  unit,
  disabled,
}: {
  /** What is counted ("Notice period"). Names the input and both buttons. */
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  /** Shown after the value ("days") and read with it. */
  unit?: string;
  disabled?: boolean;
}) => {
  const id = useId();
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft === null) return;
    setDraft(null);
    const n = Number(draft);
    // Empty or not a number puts the old value back, rather than writing 0.
    if (draft.trim() === '' || !Number.isFinite(n)) return;
    const next = snap(n, min, max, step);
    if (next !== value) onChange(next);
  };
  const bump = (dir: 1 | -1) => {
    setDraft(null);
    const next = snap(value + dir * step, min, max, step);
    if (next !== value) onChange(next);
  };
  // Wide enough for the largest value at the step's precision ("180.5"), with a
  // 3ch floor: sized to the digits alone, a 1–3 stepper was a 1ch sliver and the
  // steppers stacked in one card (1, 8, 12, 25) ended at four different edges.
  const decimals = String(step).split('.')[1]?.length ?? 0;
  const width = `${Math.max(3, String(Math.trunc(max)).length + (decimals ? decimals + 1 : 0))}ch`;
  return (
    <div
      role="group"
      aria-label={label}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--hf-space-1)' }}
    >
      {/* At a bound the button is aria-disabled, not disabled: it is the one
          just pressed, and a native disabled would drop focus to <body>. */}
      <IconButton
        icon="minus"
        label={`Decrease ${label}`}
        variant="secondary"
        onClick={() => bump(-1)}
        disabled={disabled || value <= min}
        focusableWhenDisabled={!disabled}
      />
      <div
        className="hf-field"
        style={{ padding: 'var(--hf-space-1)' }}
        onMouseDown={focusOwnControl}
      >
        <input
          type="number"
          inputMode="decimal"
          aria-label={label}
          aria-describedby={unit ? `${id}-unit` : undefined}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          value={draft ?? String(value)}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            // Discarding a draft uses the key, so it stops here (UI-CONVENTIONS
            // §4); with no draft, Escape is the surrounding Dialog's to close.
            if (e.key === 'Escape' && draft !== null) {
              e.preventDefault();
              e.stopPropagation();
              setDraft(null);
            }
          }}
          style={{
            ...controlStyle(true),
            width,
            flex: 'none',
            textAlign: 'center',
            fontVariantNumeric: 'tabular-nums',
          }}
        />
      </div>
      <IconButton
        icon="plus"
        label={`Increase ${label}`}
        variant="secondary"
        onClick={() => bump(1)}
        disabled={disabled || value >= max}
        focusableWhenDisabled={!disabled}
      />
      {unit && (
        <span
          id={`${id}-unit`}
          style={{
            marginLeft: 'var(--hf-space-1)',
            fontSize: 'var(--hf-text-sm)',
            color: 'var(--hf-ink-3)',
          }}
        >
          {unit}
        </span>
      )}
    </div>
  );
};
